// One line of a `form.draft` card (SCRUM-278).
//
// Design system §5 "Forms": label above in `app-fg-muted`, 44px control under
// 640px (touch) and 40px above, focus ring `brand`, the error in `app-danger`
// under the field.

import type { ReactNode } from "react";

import type { FormDraftField } from "@/components/generative-ui/envelope/types";
import { cn } from "@/lib/utils";

import { displayValue, maskInput } from "./masks";

const CONTROL =
  "h-11 sm:h-10 w-full rounded-lg border border-app-border bg-app-surface px-3 text-sm text-app-fg-strong " +
  "placeholder:text-app-fg-subtle focus-visible:outline-none focus-visible:ring-2 " +
  "focus-visible:ring-brand focus-visible:ring-offset-1 focus-visible:ring-offset-app-surface";

const PLACEHOLDER: Partial<Record<FormDraftField["type"], string>> = {
  cpf: "000.000.000-00",
  phone: "(00) 00000-0000",
  date: "dd/mm/aaaa",
  time: "hh:mm",
};

type RowField = Pick<FormDraftField, "key" | "label" | "type" | "required" | "editable" | "options" | "previous">;

export function FormFieldRow({
  field,
  value,
  error,
  editing,
  onChange,
  idPrefix = "form-field",
  hint,
  hintId,
}: {
  field: RowField;
  value: string;
  error?: string;
  editing: boolean;
  onChange: (next: string) => void;
  /** Distinct per card and element so two drafts never share an input id. */
  idPrefix?: string;
  /** Rendered under the label: the provenance chip and review note of a set field. */
  hint?: ReactNode;
  /** Space-separated ids the control is described by, besides its error. */
  hintId?: string;
}) {
  const inputId = `${idPrefix}-${field.key}`;
  const errorId = `${inputId}-error`;
  const describedBy = [hintId, error ? errorId : null].filter(Boolean).join(" ") || undefined;
  const editable = editing && field.editable;
  const previous = displayValue(field.type, field.previous);
  const changed = previous.length > 0 && previous !== value;

  return (
    <div className="space-y-1">
      <label
        htmlFor={inputId}
        className="block text-[13px] leading-none text-app-fg-muted"
      >
        {field.label}
        {field.required && <span className="ml-0.5 text-app-danger">*</span>}
      </label>
      {hint}

      {editable ? (
        field.type === "select" ? (
          <select
            id={inputId}
            value={value}
            aria-invalid={Boolean(error)}
            aria-describedby={describedBy}
            aria-required={field.required || undefined}
            onChange={(event) => onChange(event.target.value)}
            className={cn(CONTROL, error && "border-app-danger")}
          >
            <option value="">Selecione</option>
            {(field.options ?? []).map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        ) : field.type === "textarea" ? (
          <textarea
            id={inputId}
            value={value}
            rows={3}
            aria-invalid={Boolean(error)}
            aria-describedby={describedBy}
            aria-required={field.required || undefined}
            onChange={(event) => onChange(event.target.value)}
            className={cn(CONTROL, "h-auto py-2", error && "border-app-danger")}
          />
        ) : (
          <input
            id={inputId}
            type="text"
            inputMode={
              field.type === "cpf" || field.type === "phone" || field.type === "date" || field.type === "time"
                ? "numeric"
                : "text"
            }
            value={value}
            placeholder={PLACEHOLDER[field.type]}
            aria-invalid={Boolean(error)}
            aria-describedby={describedBy}
            aria-required={field.required || undefined}
            onChange={(event) => onChange(maskInput(field.type, event.target.value))}
            className={cn(CONTROL, error && "border-app-danger")}
          />
        )
      ) : (
        <p
          id={inputId}
          className={cn(
            "text-sm",
            value.length > 0 ? "text-app-fg-strong" : "text-app-fg-subtle",
          )}
        >
          {value.length > 0 ? value : "—"}
        </p>
      )}

      {changed && !editable && (
        <p className="text-[11px] text-app-fg-subtle">Antes: {previous}</p>
      )}

      {error && (
        <p id={errorId} role="alert" className="text-xs text-app-danger">
          {error}
        </p>
      )}
    </div>
  );
}
